import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header/Header';
import Background from '../components/Background/Background';
import '../styles/PharmacistFinishedChangesPage.css';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../api/fetchWithAuth';

type FinishedRow = {
  workOrderId: string;
  patientId: string;
  ssn: string;
  status: string;
  createdAt: string;
  lastReviewedAt: string | null;
  rejectedItemCount: number;
};

const PharmacistFinishedChangesPage: React.FC = () => {
  const navigate = useNavigate();
  const [ssnSearch, setSsnSearch] = useState('');
  const [items, setItems] = useState<FinishedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showApproved, setShowApproved] = useState(false);
  const [activeTab, setActiveTab] = useState<'new-changes' | 'old-changes'>('old-changes');

  const API_BASE = 'https://api-staging.mitoflux.is';
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetchWithAuth(
          getAccessTokenSilently,
          `${API_BASE}/api/pharmacy/work-orders/finished`
        );
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`API ${res.status}: ${text}`);
        }
        const data = (await res.json()) as FinishedRow[];
        setItems(Array.isArray(data) ? data : []);
      } catch (e: any) {
        console.error(e);
        setError(e?.message || 'Villa kom upp');
      } finally {
        setLoading(false);
      }
    })();
  }, [getAccessTokenSilently]);

  const { rejectedRows, approvedRows } = useMemo(() => {
    const rejected = items.filter(
      (x) => x.rejectedItemCount > 0 || x.status === 'REJECTED' || x.status === 'PARTIALLY_APPROVED'
    );
    const approved = items.filter(
      (x) =>
        !(x.rejectedItemCount > 0 || x.status === 'REJECTED' || x.status === 'PARTIALLY_APPROVED')
    );
    return { rejectedRows: rejected, approvedRows: approved };
  }, [items]);

  if (loading) {
    return (
      <>
        <Header />
        <Background>
          <div className="pf-finished-container">Hleð…</div>
        </Background>
      </>
    );
  }

  return (
    <>
      <Header />

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'new-changes' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('new-changes');
            navigate('/pharmacist');
          }}
        >
          Nýjar breytingar á skömmtunum
        </button>

        <button
          className={`tab ${activeTab === 'old-changes' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('old-changes');
            navigate('/pharmacist/finished');
          }}
        >
          Fyrri breytingar
        </button>
      </div>

      <Background>
        <div className="pf-finished-container">
          <h2 className="pf-title">Afgreiddar breytingar</h2>
          <hr className="divider" />
          {/* ATH */}
          {error && <div className="pf-error">{error}</div>}

          {/* Hafnað */}
          <div className="pf-section">
            <div className="pf-section-title">Hafnað</div>

            {rejectedRows.length === 0 ? (
              <div className="pf-empty">Engar hafnaðar breytingar.</div>
            ) : (
              rejectedRows.map((c) => (
                <button
                  key={c.workOrderId}
                  className="pf-row pf-row--rejected"
                  type="button"
                  onClick={() =>
                    navigate(`/pharmacist/finished/${encodeURIComponent(c.workOrderId)}`)
                  }
                >
                  <span className="pf-row-text">{'Kennitala: ' + c.ssn}</span>
                  <span className="pf-dot">{c.rejectedItemCount || 1}</span>
                </button>
              ))
            )}
          </div>

          {/* Samþykkt */}
          <div className="pf-section pf-section--approved">
            <button
              className="pf-section-toggle"
              type="button"
              onClick={() => setShowApproved((s) => !s)}
            >
              <span>Samþykkt</span>
              <span className={`pf-arrow ${showApproved ? 'open' : ''}`}>▶</span>
            </button>

            {showApproved && (
              <>
                {approvedRows.length === 0 ? (
                  <div className="pf-empty">Engar samþykktar breytingar.</div>
                ) : (
                  approvedRows.map((c) => (
                    <button
                      key={c.workOrderId}
                      className="pf-row pf-row--approved"
                      type="button"
                      onClick={() =>
                        navigate(`/pharmacist/finished/${encodeURIComponent(c.workOrderId)}`)
                      }
                    >
                      <span className="pf-row-text">{'Kennitala: ' + c.ssn}</span>
                      <span className="pf-check">✓</span>
                    </button>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      </Background>
    </>
  );
};

export default PharmacistFinishedChangesPage;

// //json file
// import React, { useEffect, useMemo, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import Header from '../components/Header/Header';
// import Background from '../components/Background/Background';
// import SearchBar, { SearchBarProps } from '../components/SearchBar/SearchBar';
// import '../styles/PharmacistFinishedChangesPage.css';
// import { useAuth0 } from '@auth0/auth0-react';
// import { fetchWithAuth } from '../api/fetchWithAuth';

// type ChangeKind = 'add' | 'edit' | 'remove';
// type DecisionState = 'approved' | 'rejected';

// type PharmacistDecision = {
//   medicationName: string;
//   kind: ChangeKind;
//   decision: DecisionState;
//   effective?: 'immediate' | 'next_cycle' | null;
//   reason?: string | null;
//   decidedAt?: string;
// };

// type ActionItem = { name: string; instruction?: string; effective?: 'immediate' | 'next_cycle' };

// type BackendChange = {
//   name: string;
//   ssn: string;
//   date: string;
//   time: string;
//   sender: string;
//   phone: string;
//   reviewStatus?: 'pending' | 'reviewed' | 'archived';
//   reviewedAt?: string | null;

//   actions?: {
//     add?: ActionItem[];
//     edit?: ActionItem[];
//     remove?: ActionItem[];

//     // you already store this under actions.pharmacist
//     pharmacist?: PharmacistDecision[];
//   };
// };

// type RowStatus = 'approved' | 'rejected';

// type FinishedRow = BackendChange & {
//   rowStatus: RowStatus; // Hafnað vs Samþykkt
//   missingDecisions: number; // sanity indicator (should be 0 for finished)
//   sortKey: number;
// };

// const PharmacistFinishedChangesPage: React.FC = () => {
//   const navigate = useNavigate();
//   const [ssn, setSsn] = useState('');
//   const [items, setItems] = useState<BackendChange[]>([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState<string>('');
//   const [showApproved, setShowApproved] = useState(false);
//   const [activeTab, setActiveTab] = useState<'new-changes' | 'old-changes'>('old-changes');

//   const API_BASE = 'http://localhost:4000';
//   const { getAccessTokenSilently } = useAuth0();

//   useEffect(() => {
//     (async () => {
//       try {
//         setLoading(true);
//         setError('');
//         const res = await fetchWithAuth(getAccessTokenSilently, `${API_BASE}/api/changes`);
//         // if (!res.ok) throw new Error('Gat ekki sótt breytingar');
//         if (!res.ok) {
//           const text = await res.text();
//           throw new Error(`API ${res.status}: ${text}`);
//         }
//         const data: BackendChange[] = await res.json();
//         setItems(Array.isArray(data) ? data : []);
//       } catch (e: any) {
//         console.error(e);
//         setError(e?.message || 'Villa kom upp');
//       } finally {
//         setLoading(false);
//       }
//     })();
//   }, [getAccessTokenSilently]);

//   const finishedRows: FinishedRow[] = useMemo(() => {
//     const asArray = <T,>(v: T[] | undefined): T[] => (Array.isArray(v) ? v : []);

//     const toSortKey = (c: BackendChange) => {
//       // build sortable key from date+time (YYMMDDHHmm)
//       const [d, m, y] = (c.date || '').split('.').map((x) => parseInt(x, 10));
//       const [hh, mm] = (c.time || '').split(':').map((x) => parseInt(x, 10));
//       const yy = 2000 + (isNaN(y) ? 0 : y);
//       return new Date(yy, (m || 1) - 1, d || 1, hh || 0, mm || 0).getTime();
//     };

//     return items
//       .filter((c) => c.reviewStatus === 'reviewed' || c.reviewStatus === 'archived')
//       .map((c) => {
//         const acts = c.actions ?? {};
//         const add = asArray(acts.add);
//         const edit = asArray(acts.edit);
//         const remove = asArray(acts.remove);
//         const pharmacist = asArray(acts.pharmacist);

//         // what meds were actionable in this change request (unique by name)
//         const actionable = new Set<string>([
//           ...add.map((a) => a.name),
//           ...edit.map((a) => a.name),
//           ...remove.map((a) => a.name),
//         ]);

//         const decisionByName = new Map<string, PharmacistDecision>();
//         pharmacist.forEach((p) => {
//           if (p?.medicationName) decisionByName.set(p.medicationName, p);
//         });

//         let rejected = false;
//         let missing = 0;

//         actionable.forEach((name) => {
//           const d = decisionByName.get(name);
//           if (!d) {
//             missing += 1;
//             return;
//           }
//           if (d.decision === 'rejected') rejected = true;
//         });

//         // If any rejected → Hafnað. Otherwise → Samþykkt.
//         // (Your rule: all must be approved to be considered samþykkt)
//         const rowStatus: RowStatus = rejected ? 'rejected' : 'approved';

//         return {
//           ...c,
//           rowStatus,
//           missingDecisions: missing,
//           sortKey: toSortKey(c),
//         };
//       })
//       .sort((a, b) => b.sortKey - a.sortKey); // newest first
//   }, [items, getAccessTokenSilently]);

//   const rejectedRows = finishedRows.filter((r) => r.rowStatus === 'rejected');
//   const approvedRows = finishedRows.filter((r) => r.rowStatus === 'approved');

//   if (loading) {
//     return (
//       <>
//         <Header title=" Rx" userName="Jón Jónsson" />
//         <Background>
//           <div className="pf-finished-container">Hleð breytingum…</div>
//         </Background>
//       </>
//     );
//   }

//   return (
//     <>
//       <Header title=" Rx" userName="Jón Jónsson" />
//       <div className="tabs">
//         <button
//           className={`tab ${activeTab === 'new-changes' ? 'active' : ''}`}
//           onClick={() => {
//             setActiveTab('new-changes');
//             navigate('/pharmacist');
//           }}
//         >
//           Nýjar breytingar á skömmtunum
//         </button>
//         <button
//           className={`tab ${activeTab === 'old-changes' ? 'active' : ''}`}
//           onClick={() => {
//             setActiveTab('old-changes');
//             navigate('/pharmacist/finished');
//           }}
//         >
//           Fyrri breytingar
//         </button>
//       </div>
//       <Background>
//         <div className="pf-finished-container">
//           <h2 className="pf-title">Afgreiddar breytingar</h2>
//           <hr className="divider" />

//           {error && <div className="pf-error">{error}</div>}

//           {/* Hafnað */}
//           <div className="pf-section">
//             <div className="pf-section-title">Hafnað</div>

//             {rejectedRows.length === 0 ? (
//               <div className="pf-empty">Engar hafnaðar breytingar.</div>
//             ) : (
//               rejectedRows.map((c, idx) => (
//                 <button
//                   key={`${c.ssn}-${c.sortKey}-${idx}`}
//                   className="pf-row pf-row--rejected"
//                   type="button"
//                   onClick={() =>
//                     navigate(
//                       `/doctor/changes/${c.ssn}?at=${encodeURIComponent(c.time)}&date=${encodeURIComponent(c.date)}`
//                     )
//                   }
//                   // ^ ATH if you already have DoctorChangeDetailPage route;
//                   // change this to your pharmacist detail route if you prefer.
//                 >
//                   <span className="pf-row-text">
//                     {c.name} - {c.ssn}
//                   </span>

//                   {/* red bubble: count of rejected meds */}
//                   <span className="pf-dot">
//                     {(c.actions?.pharmacist ?? []).filter((p) => p.decision === 'rejected')
//                       .length || 1}
//                   </span>
//                 </button>
//               ))
//             )}
//           </div>

//           {/* Sent-like is omitted per your request */}

//           {/* Samþykkt (collapsible like your screenshots) */}
//           <div className="pf-section pf-section--approved">
//             <button
//               className="pf-section-toggle"
//               type="button"
//               onClick={() => setShowApproved((s) => !s)}
//             >
//               <span>Samþykkt</span>
//               <span className={`pf-arrow ${showApproved ? 'open' : ''}`}>▶</span>
//             </button>

//             {showApproved && (
//               <>
//                 {approvedRows.length === 0 ? (
//                   <div className="pf-empty">Engar samþykktar breytingar.</div>
//                 ) : (
//                   approvedRows.map((c, idx) => (
//                     <button
//                       key={`${c.ssn}-${c.sortKey}-${idx}`}
//                       className="pf-row pf-row--approved"
//                       type="button"
//                       onClick={() =>
//                         navigate(
//                           `/doctor/changes/${c.ssn}?at=${encodeURIComponent(c.time)}&date=${encodeURIComponent(c.date)}`
//                         )
//                       }
//                     >
//                       <span className="pf-row-text">
//                         {c.name} - {c.ssn}
//                       </span>
//                       <span className="pf-check">✓</span>
//                     </button>
//                   ))
//                 )}
//               </>
//             )}
//           </div>
//         </div>
//       </Background>
//     </>
//   );
// };

// export default PharmacistFinishedChangesPage;
